import { memo } from 'react';
import { GRADIENT_PRIMARY_CSS } from '../foundation/tokens.js';

/**
 * Mapeo API ann (PascalCase) → token interno — ver `icon-highlight.styled.js`.
 */
export const iconHighlightBgTokens = {
  Success: 'success',
  Primary: 'primary',
  Alert: 'alert',
  Error: 'error',
  Neutral: 'neutral',
  Gradient: 'gradient',
  Transparent: 'transparent',
  Purple: 'purple',
  LightBlue: 'lightBlue',
  LimeGreen: 'limeGreen',
  Yellow: 'yellow',
};

export const iconHighlightIconTokens = {
  Success: 'success',
  Primary: 'primary',
  PrimaryAlt: 'primary-alt',
  PrimaryLighter: 'primary-lighter',
  Error: 'error',
  Alert: 'alert',
  Neutral: 'neutral-white',
  Green: 'green',
  Purple: 'purple',
  LightBlue: 'lightBlue',
  LimeGreen: 'limeGreen',
  Yellow: 'yellow',
};

/** Light + dark: superficies e iconos legibles sobre `dark` del shell */
const BG_CLASS = {
  success: 'bg-[#E5FDF8] dark:bg-emerald-950/35',
  primary: 'bg-[#E9F1FF] dark:bg-blue-950/40',
  alert: 'bg-[#FFF4E5] dark:bg-amber-950/35',
  error: 'bg-[#FDEDED] dark:bg-red-950/35',
  neutral: 'bg-[#F2F2F2] dark:bg-gray-800',
  purple: 'bg-[#E4CEFC] dark:bg-purple-950/40',
  lightBlue: 'bg-[#CCF1FF] dark:bg-sky-950/40',
  limeGreen: 'bg-[#E7F9B5] dark:bg-lime-950/30',
  yellow: 'bg-[#FFF4E5] dark:bg-amber-950/30',
  transparent: 'bg-transparent',
};

const ICON_CLASS = {
  success: 'text-[#003328] dark:text-emerald-200',
  primary: 'text-[#04245C] dark:text-blue-100',
  'primary-alt': 'text-[#2173FF] dark:text-blue-300',
  'primary-lighter': 'text-[#80AFFF] dark:text-blue-200',
  error: 'text-[#5F2120] dark:text-red-200',
  alert: 'text-[#663C00] dark:text-amber-200',
  'neutral-white': 'text-[#FFFFFF] dark:text-gray-100',
  green: 'text-[#009978] dark:text-emerald-300',
  purple: 'text-[#2C075C] dark:text-purple-200',
  lightBlue: 'text-[#00B5D9] dark:text-sky-200',
  limeGreen: 'text-[#A0DA1D] dark:text-lime-200',
  yellow: 'text-[#FF9900] dark:text-amber-300',
};

const SIZE = {
  small: { boxClass: 'h-6 w-6 min-h-6 min-w-6', icon: 16 },
  medium: { boxClass: 'h-10 w-10 min-h-10 min-w-10', icon: 24 },
  large: { boxClass: 'h-14 w-14 min-h-14 min-w-14', icon: 32 },
};

function resolveBgToken(bgColor) {
  if (bgColor == null || bgColor === '') return 'primary';
  return iconHighlightBgTokens[bgColor] ?? bgColor;
}

function resolveIconToken(iconColor) {
  if (iconColor == null || iconColor === '') return 'primary-alt';
  return iconHighlightIconTokens[iconColor] ?? iconColor;
}

function isLikelyCssColor(value) {
  if (typeof value !== 'string') return false;
  const v = value.trim();
  return (
    v.startsWith('#') ||
    v.startsWith('rgb') ||
    v.startsWith('hsl') ||
    v.startsWith('var(')
  );
}

function backgroundClassAndStyle(bgColor) {
  const token = resolveBgToken(bgColor);
  if (isLikelyCssColor(bgColor) && !iconHighlightBgTokens[bgColor]) {
    return { className: '', style: { backgroundColor: bgColor } };
  }
  if (token === 'gradient') {
    return {
      className: 'dark:brightness-90 dark:saturate-75',
      style: {
        backgroundImage: GRADIENT_PRIMARY_CSS,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      },
    };
  }
  if (BG_CLASS[token]) {
    return { className: BG_CLASS[token], style: undefined };
  }
  return {
    className: '',
    style: { backgroundColor: typeof bgColor === 'string' ? bgColor : '#f5f5f5' },
  };
}

function iconClassAndStyle(iconColor) {
  const token = resolveIconToken(iconColor);
  if (isLikelyCssColor(iconColor) && !iconHighlightIconTokens[iconColor]) {
    return { className: '', style: { color: iconColor } };
  }
  if (ICON_CLASS[token]) {
    return { className: ICON_CLASS[token], style: undefined };
  }
  return {
    className: '',
    style: { color: typeof iconColor === 'string' ? iconColor : '#2173FF' },
  };
}

/**
 * Same `bgColor` / `iconColor` tokens as {@link IconHighlight} → class strings for {@link ResourceCard}
 * (`iconBg` / `iconColor` on the icon wrapper). `Gradient` uses Primary surface (no inline styles on the card).
 *
 * @param {keyof typeof iconHighlightBgTokens|string} [bgColor='Primary']
 * @param {keyof typeof iconHighlightIconTokens|string} [iconColor='PrimaryAlt']
 */
export function resourceCardIconClassesFromHighlightTokens(bgColor = 'Primary', iconColor = 'PrimaryAlt') {
  const bg = backgroundClassAndStyle(bgColor);
  const ic = iconClassAndStyle(iconColor);

  let iconBg = bg.className || '';
  if (resolveBgToken(bgColor) === 'gradient') {
    iconBg = BG_CLASS.primary;
  }
  if (!iconBg) {
    iconBg = BG_CLASS.primary;
  }

  let iconColorClasses = ic.className || '';
  if (!iconColorClasses) {
    iconColorClasses = ICON_CLASS['primary-alt'];
  }

  return { iconBg, iconColor: iconColorClasses };
}

/**
 * Contenedor redondeado (8px) con icono Lucide centrado — paridad con ann `IconHighlight`
 * (tamaños 32 / 40 / 56 px, iconos 16 / 24 / 32 px).
 *
 * ## Sin icono no hay realce
 *
 * `icon` es opcional y sin él este componente no dibuja nada. Un realce vacío
 * no significa nada — es un cuadro de color alrededor de la nada — así que lo
 * honesto es ausentarse y dejar que el contenedor cierre el hueco.
 *
 * Lo que importa es que **ausentarse no es caerse**. Hasta 2026-10-08 un `icon`
 * nulo llegaba a `<Icon />` sin guarda y React respondía
 * `Element type is invalid … but got: null`, que no es un icono que falta: es
 * el árbol entero desmontado. Lo encontró el grid de tipos del Widget Builder,
 * donde `widgetTypeIconByRenderer` devuelve `null` a propósito para un
 * `rendererKey` que su mapa no conoce — justo el caso que el catálogo puede
 * crear cualquier día sembrando un tipo nuevo, y que ocurrió con `KPI_2`. La
 * promesa de aquel mapa («el tile se dibuja sin icono en vez de no dibujarse»)
 * dependía de una guarda que vivía aquí y no existía.
 *
 * Por eso la guarda es de este lado y no del llamador. Un icono que falta es un
 * dato que falta, y ningún dato que falta debería poder tumbar una página: el
 * día que otro consumidor pase un icono condicional, se ausenta un realce y no
 * se pierde una pantalla.
 *
 * @param {object} props
 * @param {import('lucide-react').LucideIcon} [props.icon] Componente icono
 *   Lucide. Sin él no se renderiza nada.
 * @param {keyof typeof iconHighlightBgTokens|string} [props.bgColor='Primary']
 * @param {keyof typeof iconHighlightIconTokens|string} [props.iconColor='PrimaryAlt']
 * @param {'small'|'medium'|'large'} [props.size='medium']
 */
const IconHighlight = ({
  icon: Icon,
  bgColor = 'Primary',
  iconColor = 'PrimaryAlt',
  size = 'medium',
  className = '',
  ...rest
}) => {
  // Ver la nota de arriba: sin icono no hay nada que realzar, y pintarlo igual
  // es `Element type is invalid`. No hay hooks encima, así que salir aquí es
  // seguro.
  if (!Icon) return null;

  const s = SIZE[size] ?? SIZE.medium;
  const bg = backgroundClassAndStyle(bgColor);
  const ic = iconClassAndStyle(iconColor);

  return (
    <div
      className={[
        'box-border inline-flex shrink-0 items-center justify-center rounded-lg p-2',
        s.boxClass,
        bg.className,
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      style={bg.style}
      {...rest}
    >
      <Icon
        size={s.icon}
        className={['shrink-0', ic.className].filter(Boolean).join(' ')}
        style={ic.style}
        strokeWidth={2}
      />
    </div>
  );
};

const MemoIconHighlight = memo(IconHighlight);
MemoIconHighlight.displayName = 'IconHighlight';

export default MemoIconHighlight;
